import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, money } from "../../lib/api";
import { STATUS_LABELS } from "../../lib/i18n";
import { Btn, PageHeader } from "../../components/admin/ui";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";

export const STATUS_COLORS = {
  new: "bg-blue-50 text-blue-700", pending_payment: "bg-amber-50 text-amber-700", paid: "bg-emerald-50 text-emerald-700",
  processing: "bg-violet-50 text-violet-700", shipped: "bg-sky-50 text-sky-700", completed: "bg-green-50 text-green-700",
  cancelled: "bg-gray-100 text-gray-500", refunded: "bg-rose-50 text-rose-700",
};

export const StatusBadge = ({ s }) => <span data-testid="order-status-badge" className={`rounded px-2 py-0.5 text-xs ${STATUS_COLORS[s]}`}>{STATUS_LABELS[s]?.ar}</span>;

export default function Orders() {
  const [sp, setSp] = useSearchParams();
  const status = sp.get("status") || "";
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/admin/orders", { params: { status: status || undefined, q: q || undefined } }).then((r) => setRows(r.data));
  }, [status, q]);

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/orders/export", { params: { status: status || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qamar-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("تعذّر تصدير الملف");
    }
  };

  return (
    <div data-testid="admin-orders">
      <PageHeader title="الطلبات" desc={`${rows.length} طلب`}>
        <Btn variant="light" data-testid="export-orders-csv-button" onClick={exportCsv}><Download size={16} />تصدير CSV</Btn>
      </PageHeader>
      <div className="mb-4 flex flex-wrap gap-2">
        <Input data-testid="orders-search-input" placeholder="رقم الطلب، الاسم أو الجوال" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs bg-white" />
        {["", ...Object.keys(STATUS_LABELS)].map((s) => (
          <button key={s} data-testid={`orders-filter-${s || "all"}`} onClick={() => setSp(s ? { status: s } : {})}
            className={`rounded border px-3 text-sm ${status === s ? "border-ink bg-ink text-white" : "border-gray-300 bg-white"}`}>{s ? STATUS_LABELS[s].ar : "الكل"}</button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500"><tr className="text-right"><th className="p-3">رقم الطلب</th><th>العميلة</th><th>المدينة</th><th>التاريخ</th><th>الإجمالي</th><th>الدفع</th><th>الحالة</th></tr></thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.order_number} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="p-3"><Link to={`/admin/orders/${o.order_number}`} data-testid={`order-row-${o.order_number}`} className="font-mono font-bold underline-offset-4 hover:underline">{o.order_number}</Link></td>
                <td>{o.customer.name}<div className="text-xs text-gray-500" dir="ltr">{o.customer.phone}</div></td>
                <td>{o.address.city}</td>
                <td className="text-xs">{new Date(o.created_at).toLocaleString("ar-SA")}</td>
                <td className="font-medium">{money(o.total)} ر.س</td>
                <td className="text-xs">{o.payment_method === "online" ? "إلكتروني" : "عند الاستلام"} · {o.payment_status === "paid" ? "مدفوع" : o.payment_status === "refunded" ? "مسترد" : "غير مدفوع"}</td>
                <td><StatusBadge s={o.status} /></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="p-10 text-center text-gray-500">لا توجد طلبات</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
